import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Animal3DCanvas, Animal3DHintBadge } from "../3d/Animal3DCanvas";
import { useDressupStore } from "../../store/dressup";

type Props = {
  screenX: number;
  screenY: number;
  mapScale: number;
  currentAreaName: string;
  isUnlocked: boolean;
  onPawnTap?: () => void;
};

export function PlayerMapPawn({
  screenX,
  screenY,
  mapScale,
  currentAreaName,
  isUnlocked,
  onPawnTap,
}: Props) {
  const store = useDressupStore();
  const [celebrate, setCelebrate] = useState(false);

  function handleTap(e: React.MouseEvent) {
    e.stopPropagation();
    setCelebrate(true);
    setTimeout(() => setCelebrate(false), 2400);
    onPawnTap?.();
  }

  const displayScale = Math.min(1.4, Math.max(0.65, mapScale));

  return (
    <div
      onClick={handleTap}
      className="absolute pointer-events-auto cursor-pointer select-none z-20 flex flex-col items-center origin-bottom transition-transform duration-75"
      style={{
        left: screenX,
        top: screenY,
        transform: `translate(-50%, -78%) scale(${displayScale})`,
      }}
      title="あなたの着せ替えアバター駒（タップでジャンプ！）"
    >
      {/* ヒント札は駒の真上に置く（canvas 下端だと足元の ST.01 の丸に重なるため）。流れの外に置き、駒の位置合わせを変えない */}
      <Animal3DHintBadge className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2" blur={false} />

      {/* 頭上のナビゲーション吹き出し */}
      <motion.div
        animate={{ y: [0, -4, 0] }}
        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        className="flex flex-col items-center mb-1"
      >
        <div
          className="bg-[#fffdf7]/95 backdrop-blur-md border-2 border-[#d9b45f] px-3 py-1 rounded-2xl text-[11px] font-black text-[#8f332c] shadow-lg flex items-center gap-1.5 whitespace-nowrap"
          style={{
            boxShadow: "0 6px 16px rgba(60, 20, 0, 0.2)",
          }}
        >
          <span className="text-sm">📍</span>
          <span>
            {isUnlocked ? `${currentAreaName}に挑戦中！🐾` : "がんばろう！"}
          </span>
          <span className="bg-[#8f332c] text-white text-[9px] px-1.5 py-0.2 rounded-full">
            現在地
          </span>
        </div>
        <div className="w-0 h-0 border-t-[6px] border-t-[#d9b45f] border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent" />
      </motion.div>

      {/* タップリアクション吹き出し */}
      <AnimatePresence>
        {celebrate && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.8 }}
            animate={{ opacity: 1, y: -20, scale: 1.1 }}
            exit={{ opacity: 0, y: -30, scale: 0.9 }}
            className="absolute -top-12 z-30 bg-rose-500 text-white font-black text-xs px-3 py-1 rounded-full shadow-lg whitespace-nowrap"
          >
            🌸 いっしょに進もうワン！✨
          </motion.div>
        )}
      </AnimatePresence>

      {/* 3D 着せ替えどうぶつモデル（Canvas） */}
      <div className="w-32 h-36 relative flex items-center justify-center filter drop-shadow-lg">
        <Animal3DCanvas
          animalType={store.animalType}
          headwear={store.headwear}
          outfit={store.outfit}
          accessory={store.accessory}
          colorTint={store.colorTint}
          height={144}
          allowOrbit={false}
          autoWave={true}
          lite
          hint={false}
        />
      </div>

      {/* マス上の丸い台座リング */}
      <div className="w-20 h-4 rounded-full bg-radial from-[#d9b45f]/60 to-transparent border border-[#d9b45f]/40 -mt-2 shadow-sm" />
    </div>
  );
}
